import React from "react";
import PropTypes from "prop-types";
import { chain, cloneDeep, find } from "lodash";
import cx from "classnames";
import { Responsive } from "react-grid-layout";
import { VisualizationWidget, TextboxWidget, RestrictedWidget } from "@/components/dashboards/dashboard-widget";
import { FiltersType } from "@/components/Filters";
import cfg from "@/config/dashboard-grid-options";
import resizeObserver from "@/services/resizeObserver";
import AutoHeightController from "./AutoHeightController";
import { WidgetTypeEnum } from "@/services/widget";

import "react-grid-layout/css/styles.css";
import "./dashboard-grid.less";

const ResponsiveGrid = Responsive;

const WidgetType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  options: PropTypes.shape({
    position: PropTypes.shape({
      col: PropTypes.number.isRequired,
      row: PropTypes.number.isRequired,
      sizeY: PropTypes.number.isRequired,
      minSizeY: PropTypes.number.isRequired,
      maxSizeY: PropTypes.number.isRequired,
      sizeX: PropTypes.number.isRequired,
      minSizeX: PropTypes.number.isRequired,
      maxSizeX: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
});

const SINGLE = "single-column";
const MULTI = "multi-column";

const DashboardWidget = React.memo(
  function DashboardWidget({
    widget,
    dashboard,
    onLoadWidget,
    onRefreshWidget,
    onRemoveWidget,
    onParameterMappingsChange,
    isEditing,
    canEdit,
    isPublic,
    isLoading,
    filters,
  }) {
    const { type } = widget;
    const onLoad = () => onLoadWidget(widget);
    const onRefresh = () => onRefreshWidget(widget);
    const onDelete = () => onRemoveWidget(widget.id);

    if (type === WidgetTypeEnum.VISUALIZATION) {
      return (
        <VisualizationWidget
          widget={widget}
          dashboard={dashboard}
          filters={filters}
          isEditing={isEditing}
          canEdit={canEdit}
          isPublic={isPublic}
          isLoading={isLoading}
          onLoad={onLoad}
          onRefresh={onRefresh}
          onDelete={onDelete}
          onParameterMappingsChange={onParameterMappingsChange}
        />
      );
    }
    if (type === WidgetTypeEnum.TEXTBOX) {
      return <TextboxWidget widget={widget} canEdit={canEdit} isPublic={isPublic} onDelete={onDelete} />;
    }
    return <RestrictedWidget widget={widget} />;
  },
  (prevProps, nextProps) =>
    prevProps.widget === nextProps.widget &&
    prevProps.canEdit === nextProps.canEdit &&
    prevProps.isPublic === nextProps.isPublic &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.filters === nextProps.filters &&
    prevProps.isEditing === nextProps.isEditing
);

class DashboardGrid extends React.Component {
  static propTypes = {
    isEditing: PropTypes.bool.isRequired,
    isPublic: PropTypes.bool,
    dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    widgets: PropTypes.arrayOf(WidgetType).isRequired,
    filters: FiltersType,
    onBreakpointChange: PropTypes.func,
    onLoadWidget: PropTypes.func,
    onRefreshWidget: PropTypes.func,
    onRemoveWidget: PropTypes.func,
    onLayoutChange: PropTypes.func,
    onParameterMappingsChange: PropTypes.func,
  };

  static defaultProps = {
    isPublic: false,
    filters: [],
    onLoadWidget: () => {},
    onRefreshWidget: () => {},
    onRemoveWidget: () => {},
    onLayoutChange: () => {},
    onBreakpointChange: () => {},
    onParameterMappingsChange: () => {},
  };

  static normalizeFrom(widget) {
    const {
      id,
      options: { position: pos },
    } = widget;

    return {
      i: id.toString(),
      x: pos.col,
      y: pos.row,
      w: pos.sizeX,
      h: pos.sizeY,
      minW: pos.minSizeX,
      maxW: pos.maxSizeX,
      minH: pos.minSizeY,
      maxH: pos.maxSizeY,
    };
  }

  containerRef = React.createRef();

  mode = null;

  autoHeightCtrl = null;

  constructor(props) {
    super(props);

    this.state = {
      layouts: props.dashboard.widgets ? { [MULTI]: props.widgets.map(DashboardGrid.normalizeFrom) } : {},
      disableAnimations: true,
      actualWidth: document.body.offsetWidth, // Initialize with a sensible default
      currentMode: MULTI,
    };

    // init AutoHeightController
    this.autoHeightCtrl = new AutoHeightController(this.onWidgetHeightUpdated);
    this.autoHeightCtrl.update(this.props.widgets);
  }

  componentDidMount() {
    const initialMode = document.body.offsetWidth <= cfg.mobileBreakPoint ? SINGLE : MULTI;
    this.onBreakpointChange(initialMode);
    // Work-around to disable initial animation on widgets; `measureBeforeMount` doesn't work properly:
    // it disables animation, but it cannot detect scrollbars.
    setTimeout(() => {
      if (this._isMounted) {
        this.setState({ disableAnimations: false });
      }
    }, 50);

    this._isMounted = true;
    this.disconnectResizeObserver = resizeObserver(this.containerRef.current, () => {
      if (this.containerRef.current) {
        this.setState({ actualWidth: this.containerRef.current.offsetWidth });
      }
    });
  }

  componentDidUpdate() {
    // update, in case widgets added or removed
    this.autoHeightCtrl.update(this.props.widgets);
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.autoHeightCtrl.destroy();
    if (this.disconnectResizeObserver) {
      this.disconnectResizeObserver();
    }
  }

  onLayoutChange = (_, layouts) => {
    // Merge layouts into state to avoid wiping out other modes (like SINGLE)
    this.setState(prevState => ({
      layouts: { ...prevState.layouts, ...layouts },
    }));

    // workaround for https://github.com/STRML/react-grid-layout/issues/889
    // remove next line when fix lands
    const currentMode = this.state.actualWidth <= cfg.mobileBreakPoint ? SINGLE : MULTI;
    // end workaround

    // don't save single column mode layout
    if (currentMode === SINGLE) {
      return;
    }

    const multiLayout = layouts[MULTI] || this.state.layouts[MULTI];
    if (!multiLayout) {
      return;
    }

    const normalized = chain(multiLayout)
      .keyBy("i")
      .mapValues(this.normalizeTo)
      .value();

    this.props.onLayoutChange(normalized);
  };

  onBreakpointChange = mode => {
    this.mode = mode;
    this.setState({ currentMode: mode });
    this.props.onBreakpointChange(mode === SINGLE);
  };

  // height updated by auto-height
  onWidgetHeightUpdated = (widgetId, newHeight) => {
    this.setState(({ layouts, currentMode }) => {
      const modeToUpdate = currentMode === SINGLE ? SINGLE : MULTI;
      const layout = cloneDeep(layouts[modeToUpdate] || []);
      const item = find(layout, { i: widgetId.toString() });
      if (item) {
        // update widget height
        item.h = Math.ceil((newHeight + cfg.margins) / cfg.rowHeight);
      }

      return { layouts: { ...layouts, [modeToUpdate]: layout } };
    });
  };

  // height updated by manual resize
  onWidgetResize = (layout, oldItem, newItem) => {
    if (oldItem.h !== newItem.h) {
      this.autoHeightCtrl.remove(Number(newItem.i));
    }

    this.autoHeightCtrl.resume();
  };

  normalizeTo = layout => ({
    col: layout.x,
    row: layout.y,
    sizeX: layout.w,
    sizeY: layout.h,
    autoHeight: this.autoHeightCtrl.exists(layout.i),
  });

  render() {
    const {
      onLoadWidget,
      onRefreshWidget,
      onRemoveWidget,
      onParameterMappingsChange,
      filters,
      dashboard,
      isPublic,
      isEditing,
      widgets,
    } = this.props;
    const className = cx("dashboard-wrapper", isEditing ? "editing-mode" : "preview-mode");

    const { actualWidth } = this.state;
    const mobileBreakPoint = cfg.mobileBreakPoint || 800;
    const scalingThreshold = cfg.scalingThreshold || 1200;

    let scale = 1.0;
    let effectiveWidth = actualWidth;

    if (actualWidth > mobileBreakPoint && actualWidth < scalingThreshold) {
      scale = actualWidth / scalingThreshold;
      effectiveWidth = scalingThreshold;
    }

    // Calculate height adjustment to prevent empty space.
    // If we don't have current layout in state yet, estimate it from widgets prop.
    const currentLayout = this.state.layouts[this.state.currentMode] || widgets.map(DashboardGrid.normalizeFrom);
    const maxRow = currentLayout.reduce((acc, item) => Math.max(acc, (item.y || 0) + (item.h || 0)), 0);
    const gridHeight = maxRow > 0 ? maxRow * cfg.rowHeight + cfg.margins : 0;
    const scaledHeight = gridHeight * scale;

    const scalerStyle = {
      transform: `scale(${scale})`,
      transformOrigin: "top left",
      width: scale === 1.0 ? "100%" : effectiveWidth,
    };

    const containerStyle = scale !== 1.0 ? { height: Math.ceil(scaledHeight), overflow: "hidden" } : {};

    return (
      <div className={className} ref={this.containerRef} style={containerStyle}>
        <div className="dashboard-grid-scaler" style={scalerStyle}>
          <ResponsiveGrid
            draggableCancel="input,.sortable-container"
            className={cx("layout", { "disable-animations": this.state.disableAnimations })}
            cols={{ [MULTI]: cfg.columns, [SINGLE]: 1 }}
            rowHeight={cfg.rowHeight - cfg.margins}
            margin={[cfg.margins, cfg.margins]}
            isDraggable={isEditing}
            isResizable={isEditing}
            containerPadding={[0, 0]}
            onResizeStart={this.autoHeightCtrl.stop}
            onResizeStop={this.onWidgetResize}
            layouts={this.state.layouts}
            onLayoutChange={this.onLayoutChange}
            onBreakpointChange={this.onBreakpointChange}
            breakpoints={{ [MULTI]: cfg.mobileBreakPoint, [SINGLE]: 0 }}
            width={effectiveWidth}
            transformScale={scale}>
            {widgets.map(widget => (
              <div
                key={widget.id}
                data-grid={DashboardGrid.normalizeFrom(widget)}
                data-widgetid={widget.id}
                data-test={`WidgetId${widget.id}`}
                className={cx("dashboard-widget-wrapper", {
                  "widget-auto-height-enabled": this.autoHeightCtrl.exists(widget.id),
                })}>
                <DashboardWidget
                  dashboard={dashboard}
                  widget={widget}
                  filters={filters}
                  isPublic={isPublic}
                  isLoading={widget.loading}
                  isEditing={isEditing}
                  canEdit={dashboard.canEdit()}
                  onLoadWidget={onLoadWidget}
                  onRefreshWidget={onRefreshWidget}
                  onRemoveWidget={onRemoveWidget}
                  onParameterMappingsChange={onParameterMappingsChange}
                />
              </div>
            ))}
          </ResponsiveGrid>
        </div>
      </div>
    );
  }
}

export default DashboardGrid;
